import { useMemo } from 'react';
import type { FormEvent, ChangeEvent } from 'react';
import { useParams, useSearchParams, useNavigate, Navigate } from 'react-router-dom';
import { Box, Button, Stack, SvgIcon, Typography } from '@mui/material';
import ArrowRightIcon from '@untitled-ui/icons-react/build/esm/ArrowRight';
import { RouterLink } from 'src/components/router-link';
import { Seo } from 'src/components/seo';
import { BlogCover } from 'src/components/blog-cover';
import { usePageView } from 'src/hooks/use-page-view';
import { paths } from 'src/paths';
import type { Page as PageType } from 'src/types/page';
import { blogPosts } from 'src/data/blog-posts';
import type { BlogPost } from 'src/data/blog-posts';

// --- Mystic Media Film style tokens (matched to the homepage) ---
const SANS = '"Inter", sans-serif';
const MONO = '"Roboto Mono", monospace';
const ACCENT = '#2563eb';
const ACCENT_DEEP = '#1d4ed8';
const FG = '#0a0a0b';
const MUTED = '#52525b';
const MUTED2 = '#a1a1aa';
const LINE = '#e4e4e7';

const POSTS_PER_PAGE = 9; // page 1 = 1 featured + up to 8 grid; later pages = up to 9 grid

const container = { maxWidth: 1200, mx: 'auto', px: { xs: 3, sm: 5 } } as const;
const dividerLine = { height: '1px', width: '100%', background: LINE } as const;

const formatDate = (d: string) =>
  new Date(d).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });

const SITE = 'https://mysticmediafilm.com';

// Mystic "SectionLabel" eyebrow: tick line + Roboto Mono uppercase.
const SectionLabel = ({ children }: { children: string }) => (
  <Stack direction="row" alignItems="center" spacing={1.25}>
    <Box sx={{ width: 24, height: 1, background: FG }} />
    <Typography
      sx={{
        fontFamily: MONO,
        fontSize: 11,
        fontWeight: 500,
        color: FG,
        letterSpacing: 1.4,
        textTransform: 'uppercase'
      }}
    >
      {children}
    </Typography>
  </Stack>
);

const Page: PageType = () => {
  usePageView();
  const navigate = useNavigate();
  const { page: pageParam } = useParams<{ page: string }>();
  const [searchParams] = useSearchParams();

  const q = (searchParams.get('q') || '').trim();
  const searching = q.length > 0;
  const pageNum = Math.max(1, parseInt(pageParam || '1', 10) || 1);

  const filtered = useMemo<BlogPost[]>(() => {
    // Newest first by publish date (sorts a copy; leaves the source array intact).
    const byNewest = [...blogPosts].sort((a, b) => b.date.localeCompare(a.date));
    if (!q) return byNewest;
    const needle = q.toLowerCase();
    return byNewest.filter((p) =>
      [p.title, p.excerpt, p.category, p.keywords].join(' ').toLowerCase().includes(needle)
    );
  }, [q]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / POSTS_PER_PAGE));

  // Redirect out-of-range page numbers back to a valid URL.
  if (pageParam && (pageNum > totalPages || pageNum < 1)) {
    return <Navigate to={searching ? `/blog?q=${encodeURIComponent(q)}` : paths.blog.index} replace />;
  }

  const start = (pageNum - 1) * POSTS_PER_PAGE;
  const pageItems = filtered.slice(start, start + POSTS_PER_PAGE);
  const showFeatured = !searching && pageNum === 1 && pageItems.length > 0;
  const featured = showFeatured ? pageItems[0] : null;
  const gridItems = showFeatured ? pageItems.slice(1) : pageItems;

  const pageUrl = (n: number) => {
    const base = n <= 1 ? paths.blog.index : `/blog/page/${n}`;
    return searching ? `${base}?q=${encodeURIComponent(q)}` : base;
  };

  const onSearch = (value: string) => {
    const v = value.trim();
    navigate(v ? `/blog?q=${encodeURIComponent(v)}` : paths.blog.index, { replace: true });
  };

  const canonical = searching || pageNum <= 1 ? `${SITE}/blog` : `${SITE}/blog/page/${pageNum}`;
  const seoTitle =
    pageNum > 1
      ? `Blog (Page ${pageNum}) | Mystic Media Film`
      : 'Blog | Mystic Media Film — Houston Web Design, Video & SEO';

  // --- small shared bits ------------------------------------------------------
  const MetaDot = () => <Box component="span" sx={{ color: ACCENT }}>·</Box>;

  const ReadArrow = ({ className }: { className?: string }) => (
    <Box
      className={className}
      component="svg"
      viewBox="0 0 24 24"
      sx={{ width: 14, height: 14, transition: 'transform 0.3s ease' }}
    >
      <path
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M7 17 L17 7 M9 7 L17 7 L17 15"
      />
    </Box>
  );

  const searchField = (
    <Box
      component="form"
      onSubmit={(e: FormEvent) => e.preventDefault()}
      sx={{ position: 'relative', width: { xs: '100%', md: 340 }, flexShrink: 0 }}
    >
      <Box
        component="svg"
        viewBox="0 0 24 24"
        aria-hidden
        sx={{
          position: 'absolute',
          left: 16,
          top: '50%',
          transform: 'translateY(-50%)',
          width: 17,
          height: 17,
          color: MUTED2,
          pointerEvents: 'none'
        }}
      >
        <path
          fill="none"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
          d="M11 4a7 7 0 1 0 0 14 7 7 0 0 0 0-14zM20 20l-3.5-3.5"
        />
      </Box>
      <Box
        component="input"
        value={q}
        onChange={(e: ChangeEvent<HTMLInputElement>) => onSearch(e.target.value)}
        placeholder="Search articles…"
        aria-label="Search articles"
        sx={{
          width: '100%',
          fontFamily: SANS,
          fontSize: 14.5,
          color: FG,
          pl: 5,
          pr: 2.5,
          py: 1.375,
          borderRadius: 999,
          border: `1px solid ${LINE}`,
          background: '#fff',
          outline: 'none',
          transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
          '&::placeholder': { color: '#9ca3af' },
          '&:focus': { borderColor: ACCENT, boxShadow: `0 0 0 3px ${ACCENT}1a` }
        }}
      />
    </Box>
  );

  const renderFeatured = (post: BlogPost) => (
    <Box
      component={RouterLink}
      href={`/blog/${post.slug}`}
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
        gap: { xs: 4, lg: 7 },
        alignItems: 'center',
        textDecoration: 'none',
        '&:hover .cover-scale': { transform: 'scale(1.04)' },
        '&:hover .post-title': { color: ACCENT },
        '&:hover .read-link': { color: ACCENT },
        '&:hover .read-arrow': { transform: 'translate(3px, -3px)' }
      }}
    >
      <Box
        sx={{
          position: 'relative',
          aspectRatio: '16 / 11',
          borderRadius: 3,
          overflow: 'hidden',
          border: `1px solid ${LINE}`
        }}
      >
        <Box className="cover-scale" sx={{ position: 'absolute', inset: 0, transition: 'transform 600ms cubic-bezier(0.22, 1, 0.36, 1)' }}>
          {post.image ? (
            <Box
              component="img"
              src={post.image}
              alt={post.title}
              loading="lazy"
              sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : (
            <BlogCover category={post.category} size="lg" caption={post.readTime} />
          )}
        </Box>
      </Box>

      <Box>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
          <Typography sx={{ fontFamily: MONO, fontSize: 11, fontWeight: 500, letterSpacing: 1, textTransform: 'uppercase', color: ACCENT }}>
            Latest
          </Typography>
          <MetaDot />
          <Typography sx={{ fontFamily: MONO, fontSize: 11, fontWeight: 500, letterSpacing: 1, textTransform: 'uppercase', color: MUTED2 }}>
            {post.category}
          </Typography>
        </Stack>
        <Typography
          className="post-title"
          component="h2"
          sx={{
            fontFamily: SANS,
            fontSize: { xs: 30, md: 44 },
            fontWeight: 700,
            lineHeight: 1.06,
            letterSpacing: '-0.035em',
            color: FG,
            transition: 'color 0.2s ease'
          }}
        >
          {post.title}
        </Typography>
        <Typography sx={{ mt: 2.5, fontFamily: SANS, fontSize: { xs: 16, md: 18 }, fontWeight: 400, lineHeight: 1.6, color: MUTED }}>
          {post.excerpt}
        </Typography>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 3 }}>
          <Typography sx={{ fontFamily: MONO, fontSize: 11, fontWeight: 500, letterSpacing: 0.8, color: MUTED2 }}>
            {formatDate(post.date)}
          </Typography>
          <MetaDot />
          <Typography sx={{ fontFamily: MONO, fontSize: 11, fontWeight: 500, letterSpacing: 0.8, color: MUTED2 }}>
            {post.readTime}
          </Typography>
        </Stack>
        <Stack className="read-link" direction="row" alignItems="center" spacing={0.75} sx={{ mt: 3.5, color: FG, transition: 'color 0.25s ease' }}>
          <Typography sx={{ fontFamily: SANS, fontSize: 14, fontWeight: 600, letterSpacing: '-0.005em', color: 'inherit' }}>
            Read the article
          </Typography>
          <ReadArrow className="read-arrow" />
        </Stack>
      </Box>
    </Box>
  );

  const renderCard = (post: BlogPost) => (
    <Box
      key={post.slug}
      component={RouterLink}
      href={`/blog/${post.slug}`}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        textDecoration: 'none',
        '&:hover .cover-scale': { transform: 'scale(1.05)' },
        '&:hover .post-title': { color: ACCENT },
        '&:hover .read-link': { color: ACCENT },
        '&:hover .read-arrow': { transform: 'translate(3px, -3px)' }
      }}
    >
      <Box
        sx={{
          position: 'relative',
          aspectRatio: '16 / 10',
          borderRadius: 3,
          overflow: 'hidden',
          border: `1px solid ${LINE}`
        }}
      >
        <Box className="cover-scale" sx={{ position: 'absolute', inset: 0, transition: 'transform 600ms cubic-bezier(0.22, 1, 0.36, 1)' }}>
          {post.image ? (
            <Box
              component="img"
              src={post.image}
              alt={post.title}
              loading="lazy"
              sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : (
            <BlogCover category={post.category} size="sm" />
          )}
        </Box>
      </Box>

      <Typography sx={{ mt: 2.5, fontFamily: MONO, fontSize: 10.5, fontWeight: 500, letterSpacing: 1, textTransform: 'uppercase', color: ACCENT }}>
        {post.category}
      </Typography>
      <Typography
        className="post-title"
        component="h3"
        sx={{
          mt: 1.25,
          fontFamily: SANS,
          fontSize: { xs: 21, md: 23 },
          fontWeight: 600,
          lineHeight: 1.18,
          letterSpacing: '-0.025em',
          color: FG,
          transition: 'color 0.2s ease'
        }}
      >
        {post.title}
      </Typography>
      <Typography sx={{ mt: 1.5, fontFamily: SANS, fontSize: 14.5, fontWeight: 400, lineHeight: 1.6, color: MUTED }}>
        {post.excerpt}
      </Typography>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 2 }}>
        <Typography sx={{ fontFamily: MONO, fontSize: 10.5, fontWeight: 500, letterSpacing: 0.8, color: MUTED2 }}>
          {formatDate(post.date)}
        </Typography>
        <MetaDot />
        <Typography sx={{ fontFamily: MONO, fontSize: 10.5, fontWeight: 500, letterSpacing: 0.8, color: MUTED2 }}>
          {post.readTime}
        </Typography>
      </Stack>
      <Stack className="read-link" direction="row" alignItems="center" spacing={0.75} sx={{ mt: 2, color: '#3f3f46', transition: 'color 0.25s ease' }}>
        <Typography sx={{ fontFamily: SANS, fontSize: 13, fontWeight: 600, letterSpacing: '-0.005em', color: 'inherit' }}>
          Read the article
        </Typography>
        <ReadArrow className="read-arrow" />
      </Stack>
    </Box>
  );

  const renderPagination = () => {
    if (totalPages <= 1) return null;
    const pages = Array.from({ length: totalPages }, (_, i) => i + 1);
    const navItem = (label: string, to: string | null) =>
      to ? (
        <Box
          component={RouterLink}
          href={to}
          sx={{
            fontFamily: MONO, fontSize: 11, fontWeight: 500, letterSpacing: 1, textTransform: 'uppercase',
            color: FG, textDecoration: 'none', px: 1, transition: 'color 0.2s ease', '&:hover': { color: ACCENT }
          }}
        >
          {label}
        </Box>
      ) : (
        <Box component="span" sx={{ fontFamily: MONO, fontSize: 11, fontWeight: 500, letterSpacing: 1, textTransform: 'uppercase', color: '#cbced4', px: 1 }}>
          {label}
        </Box>
      );

    return (
      <Stack direction="row" alignItems="center" justifyContent="center" spacing={{ xs: 0.5, sm: 1 }} sx={{ mt: { xs: 8, md: 12 } }}>
        {navItem('← Prev', pageNum > 1 ? pageUrl(pageNum - 1) : null)}
        {pages.map((n) => {
          const active = n === pageNum;
          return (
            <Box
              key={n}
              component={active ? 'span' : RouterLink}
              href={active ? undefined : pageUrl(n)}
              sx={{
                display: 'grid', placeItems: 'center', width: 40, height: 40, borderRadius: '50%',
                fontFamily: SANS, fontSize: 14, fontWeight: active ? 600 : 500, textDecoration: 'none',
                color: active ? '#fff' : FG, background: active ? FG : 'transparent',
                border: active ? `1px solid ${FG}` : `1px solid ${LINE}`, transition: 'all 0.2s ease',
                '&:hover': active ? {} : { borderColor: ACCENT, color: ACCENT }
              }}
            >
              {n}
            </Box>
          );
        })}
        {navItem('Next →', pageNum < totalPages ? pageUrl(pageNum + 1) : null)}
      </Stack>
    );
  };

  return (
    <>
      <Seo
        title={seoTitle}
        description="Practical guides on web design, video production, local SEO, branding, and AI automation for businesses in Katy, TX and across Greater Houston — from Mystic Media Film."
        keywords="Houston marketing blog, web design Houston, local SEO Houston, video production Houston, Katy TX digital agency, AI automation small business, Mystic Media Film"
        canonicalUrl={canonical}
      />
      <main style={{ background: '#ffffff' }}>
        {/* Hero */}
        <Box component="section" sx={{ pt: { xs: '128px', md: '180px' }, pb: { xs: 5, md: 7 } }}>
          <Box sx={container}>
            <SectionLabel>The Mystic Media Journal</SectionLabel>
            <Typography
              component="h1"
              sx={{
                mt: 2.5,
                fontFamily: SANS,
                fontSize: { xs: '48px', sm: '66px', md: '88px' },
                fontWeight: 700,
                lineHeight: 1.0,
                letterSpacing: '-0.04em',
                color: FG
              }}
            >
              Grow, create,{' '}
              <Box component="span" sx={{ color: ACCENT }}>inspire.</Box>
            </Typography>

            {/* Tagline left + search right, vertically in line */}
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              alignItems={{ xs: 'flex-start', md: 'center' }}
              justifyContent="space-between"
              spacing={{ xs: 3, md: 5 }}
              sx={{ mt: 3 }}
            >
              <Typography
                sx={{
                  fontFamily: SANS,
                  fontSize: { xs: 16, md: 18 },
                  fontWeight: 400,
                  lineHeight: 1.6,
                  color: MUTED,
                  maxWidth: 600
                }}
              >
                Straight answers on websites, video, SEO, branding, and AI — for businesses across
                Katy and Greater Houston, from the Mystic Media Film team.
              </Typography>
              {searchField}
            </Stack>

            <Box sx={{ ...dividerLine, mt: { xs: 5, md: 6 } }} />
          </Box>
        </Box>

        {/* Results */}
        {filtered.length === 0 ? (
          <Box component="section" sx={{ pb: { xs: 16, md: 24 } }}>
            <Box sx={container}>
              <Typography sx={{ fontFamily: SANS, fontSize: { xs: 28, md: 36 }, fontWeight: 700, letterSpacing: '-0.03em', color: FG }}>
                No articles found.
              </Typography>
              <Typography sx={{ mt: 1.5, fontFamily: SANS, fontSize: 16, color: MUTED }}>
                Try a different search, or{' '}
                <Box component={RouterLink} href={paths.blog.index} sx={{ color: ACCENT, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                  browse all articles
                </Box>
                .
              </Typography>
            </Box>
          </Box>
        ) : (
          <>
            {searching && (
              <Box component="section" sx={{ pt: 1 }}>
                <Box sx={container}>
                  <Typography sx={{ fontFamily: MONO, fontSize: 11.5, fontWeight: 500, letterSpacing: 0.8, textTransform: 'uppercase', color: MUTED2 }}>
                    {filtered.length} result{filtered.length === 1 ? '' : 's'} for “{q}” ·{' '}
                    <Box component={RouterLink} href={paths.blog.index} sx={{ color: ACCENT, textDecoration: 'none' }}>
                      Clear
                    </Box>
                  </Typography>
                </Box>
              </Box>
            )}

            {/* Featured */}
            {featured && (
              <Box component="section" sx={{ py: { xs: 5, md: 7 } }}>
                <Box sx={container}>{renderFeatured(featured)}</Box>
              </Box>
            )}

            {/* Grid */}
            <Box component="section" sx={{ pb: { xs: 12, md: 18 }, pt: featured ? 0 : { xs: 5, md: 7 } }}>
              <Box sx={container}>
                <Box sx={{ ...dividerLine, mb: { xs: 6, md: 7 } }} />
                {gridItems.length > 0 ? (
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
                      columnGap: { xs: 2.5, md: 4 },
                      rowGap: { xs: 6, md: 7 }
                    }}
                  >
                    {gridItems.map((post) => renderCard(post))}
                  </Box>
                ) : (
                  <Typography sx={{ fontFamily: SANS, fontSize: 15, color: MUTED }}>
                    That is the latest from the journal — more on the way.
                  </Typography>
                )}
                {renderPagination()}
              </Box>
            </Box>
          </>
        )}

        {/* Final CTA — Mystic dark block */}
        <Box component="section" sx={{ pb: { xs: 14, md: 20 } }}>
          <Box sx={container}>
            <Box
              sx={{
                position: 'relative',
                borderRadius: 4,
                overflow: 'hidden',
                background: FG,
                color: '#fff',
                px: { xs: 4, md: 8 },
                py: { xs: 8, md: 12 },
                textAlign: 'center'
              }}
            >
              <Box
                sx={{
                  position: 'absolute', top: '-30%', right: '-10%', width: 600, height: 600, borderRadius: '50%',
                  background: 'radial-gradient(circle, rgba(37, 99, 235, 0.4) 0%, transparent 65%)', filter: 'blur(70px)', pointerEvents: 'none'
                }}
              />
              <Box
                sx={{
                  position: 'absolute', bottom: '-30%', left: '-10%', width: 500, height: 500, borderRadius: '50%',
                  background: 'radial-gradient(circle, rgba(99, 102, 241, 0.3) 0%, transparent 65%)', filter: 'blur(80px)', pointerEvents: 'none'
                }}
              />
              <Stack spacing={3} alignItems="center" sx={{ position: 'relative', zIndex: 1 }}>
                <Typography sx={{ fontFamily: MONO, fontSize: 11, fontWeight: 500, letterSpacing: 1.4, textTransform: 'uppercase', color: '#60a5fa' }}>
                  Start Here
                </Typography>
                <Typography
                  component="h2"
                  sx={{
                    mx: 'auto', maxWidth: 800, fontFamily: SANS, fontSize: { xs: '34px', md: '60px' },
                    fontWeight: 700, lineHeight: 1.05, letterSpacing: '-0.035em', color: '#fff'
                  }}
                >
                  Have a question about growing your{' '}
                  <Box component="span" sx={{ color: 'rgba(255,255,255,0.55)' }}>Houston business?</Box>
                </Typography>
                <Typography sx={{ mx: 'auto', maxWidth: 480, fontFamily: SANS, fontSize: { xs: 16, md: 18 }, lineHeight: 1.55, color: 'rgba(255,255,255,0.6)' }}>
                  Tell us what you are working on. We will follow up within one business day — no pressure, no obligation.
                </Typography>
                <Button
                  component={RouterLink}
                  href={paths.contact}
                  endIcon={<SvgIcon sx={{ fontSize: 16 }}><ArrowRightIcon /></SvgIcon>}
                  variant="contained"
                  sx={{
                    mt: 1, fontFamily: SANS, background: '#fff', color: FG, fontWeight: 500, fontSize: 15,
                    letterSpacing: '-0.01em', px: 2.75, py: 1.5, borderRadius: 999, textTransform: 'none', boxShadow: 'none',
                    '&:hover': { background: '#f4f4f5', boxShadow: 'none' }
                  }}
                >
                  Book a free consultation
                </Button>
              </Stack>
            </Box>
          </Box>
        </Box>
      </main>
    </>
  );
};

export default Page;
